import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import { EstimatedInventoryPerOrderFrequencyTypeEnum } from '@b-stock/contract-api-client'
import { SingleLineWithEllipsis } from '@b-stock/fe-core/bstock-next/components'
import { useIsTranslationDefined } from '@b-stock/fe-core/bstock-next/hooks'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import type { SearchResult } from '@/queries/listingSearchQuery'

const DetailsContainer = styled.div`
  ${Typography.Body4_Semibold}
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  max-width: 100%;
`

const Divider = styled.span`
  height: 0.5rem;
  width: 0.0625rem;
  background: ${Colors.Semantic.Neutral.medium_grey};
`

const useContractFrequency = (
  contractFrequency: SearchResult['contractFrequency']
) => {
  const { min, max, type } = contractFrequency || {}
  const frequencyMessageId = useIsTranslationDefined(
    `Auction.ContractDetails.frequency.${type ?? EstimatedInventoryPerOrderFrequencyTypeEnum.Weeks}`
  )
  let frequencyRange: React.ReactNode = null
  if (min) {
    if (max && max !== min) {
      frequencyRange = (
        <>
          <FormattedNumber value={min} />-
          <FormattedNumber value={max} />
        </>
      )
    } else {
      frequencyRange = <FormattedNumber value={min} />
    }
  } else if (max) {
    frequencyRange = <FormattedNumber value={max} />
  }
  return frequencyMessageId && frequencyRange ? (
    <FormattedMessage
      id={frequencyMessageId}
      values={{ range: frequencyRange, upperBound: max || min || 0 }}
    />
  ) : null
}

const DurationAndFrequency = ({
  listing,
}: {
  listing: Pick<SearchResult, 'contractDuration' | 'contractFrequency'>
}) => {
  const durationMessageId = useIsTranslationDefined(
    `Auction.ContractDetails.duration.${listing.contractDuration?.durationUnits}`
  )
  let duration: React.ReactNode = null
  if (durationMessageId && listing.contractDuration?.duration) {
    duration = (
      <FormattedMessage
        id={durationMessageId}
        values={{ duration: listing.contractDuration.duration }}
      />
    )
  }

  const frequency = useContractFrequency(listing.contractFrequency)

  return (
    <DetailsContainer>
      {duration ? <div data-testid="contract-duration">{duration}</div> : null}
      {duration && frequency ? <Divider /> : null}
      {frequency ? (
        <SingleLineWithEllipsis data-testid="contract-frequency">
          {frequency}
        </SingleLineWithEllipsis>
      ) : null}
    </DetailsContainer>
  )
}

export default DurationAndFrequency
